import type { AnimalType, ColorKey, Gender, Temperament } from './constants'
import type { Animal, LostReport } from '../types/models'

export type LostListItem = LostReport & {
  animal: Animal
  thumbnail_key: string | null
}

export interface CreateLostInput {
  type: AnimalType
  color1: ColorKey
  color2?: ColorKey | null
  color3?: ColorKey | null
  chip_status: 'unknown' | 'none' | 'known'
  chip?: string | null
  gender?: Gender
  spayed?: 0 | 1 | null
  breed_other?: string | null
  contact_info: string
  temperament: Temperament
  missing_noticed_at: string
  last_known_lat: number
  last_known_lng: number
  source_locale: string
}

async function readError(response: Response): Promise<string> {
  if (response.status === 413) {
    return 'Photo is still too large after compression. Try a different photo.'
  }
  try {
    const body: unknown = await response.json()
    if (body && typeof body === 'object' && 'error' in body) {
      const message = (body as { error?: unknown }).error
      if (typeof message === 'string' && message.trim()) return message
    }
  } catch {
    // Fall through to a generic status-based message.
  }
  return `Request failed (${response.status})`
}

type LostApiRow = {
  id: number
  animal_id: number
  contact_info: string
  status: LostReport['status']
  created_at: string
  updated_at: string
  missing_noticed_at: string | null
  last_known_lat: number | null
  last_known_lng: number | null
  temperament: Temperament
  type: AnimalType
  breed_id: number | null
  color1: ColorKey
  color2: ColorKey | null
  color3: ColorKey | null
  chip: string | null
  chip_status: Animal['chip_status']
  spayed: Animal['spayed']
  gender: Animal['gender']
  ownership_status: Animal['ownership_status']
  if_found_contact: string | null
  source_locale?: string | null
  user_id?: string
  notify_on_message?: number
  thumbnail_key?: string | null
  location_fuzzed?: boolean
  notes?: string | null
}

function toLostListItem(row: LostApiRow): LostListItem {
  return {
    id: row.id,
    animal_id: row.animal_id,
    contact_info: row.contact_info,
    user_id: row.user_id ?? '',
    status: row.status,
    created_at: row.created_at,
    updated_at: row.updated_at,
    missing_noticed_at: row.missing_noticed_at,
    last_known_lat: row.last_known_lat,
    last_known_lng: row.last_known_lng,
    temperament: row.temperament,
    source_locale: row.source_locale ?? 'en',
    notify_on_message: row.notify_on_message === 1,
    location_fuzzed: Boolean(row.location_fuzzed),
    notes: row.notes ?? null,
    thumbnail_key: row.thumbnail_key ?? null,
    animal: {
      id: row.animal_id,
      type: row.type,
      breed_id: row.breed_id,
      color1: row.color1,
      color2: row.color2,
      color3: row.color3,
      chip: row.chip,
      chip_status: row.chip_status,
      spayed: row.spayed,
      gender: row.gender ?? 'unknown',
      ownership_status: row.ownership_status,
      if_found_contact: row.if_found_contact,
      moderator_notes: null,
      created_at: row.created_at,
      updated_at: row.updated_at,
    },
  }
}

export async function fetchLostReports(since?: string): Promise<LostListItem[]> {
  const params = new URLSearchParams()
  if (since) params.set('since', since)
  const query = params.toString()
  const response = await fetch(`/api/lost${query ? `?${query}` : ''}`, {
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw new Error(await readError(response))
  const body: unknown = await response.json()
  const rows =
    body && typeof body === 'object' && 'lost' in body
      ? (body as { lost?: LostApiRow[] }).lost
      : null
  return (rows ?? []).map(toLostListItem)
}

export async function fetchAnimalImages(animalId: number): Promise<string[]> {
  const response = await fetch(`/api/animals/${animalId}`, {
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw new Error(await readError(response))
  const body: unknown = await response.json()
  if (!body || typeof body !== 'object' || !('images' in body)) return []
  const images = (body as { images?: unknown }).images
  return Array.isArray(images)
    ? images.filter((key): key is string => typeof key === 'string')
    : []
}

export async function createLostReport(
  input: CreateLostInput,
): Promise<LostListItem> {
  const response = await fetch('/api/lost', {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  })
  if (!response.ok) throw new Error(await readError(response))
  const body: unknown = await response.json()
  if (!body || typeof body !== 'object') throw new Error('Lost report was not returned')
  const payload = body as { animal_id?: number; lost?: LostApiRow }
  if (!payload.lost || payload.animal_id == null || !payload.lost.type) {
    throw new Error('Lost report was not returned')
  }
  return toLostListItem({
    ...payload.lost,
    animal_id: payload.animal_id,
  })
}

export async function uploadAnimalImage(
  animalId: number,
  image: File,
): Promise<void> {
  const form = new FormData()
  form.set('animal_id', String(animalId))
  form.set('image', image)
  const response = await fetch('/api/images', {
    method: 'POST',
    credentials: 'include',
    body: form,
  })
  if (!response.ok) throw new Error(await readError(response))
}

export async function deleteLostReport(lostReportId: number): Promise<void> {
  const response = await fetch(`/api/lost/${lostReportId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw new Error(await readError(response))
}

export async function markLostReportFound(lostReportId: number): Promise<void> {
  const response = await fetch(`/api/lost/${lostReportId}/found`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw new Error(await readError(response))
}

export async function updateLostNotes(
  lostReportId: number,
  notes: string | null,
  sourceLocale: string,
): Promise<LostListItem> {
  const response = await fetch(`/api/lost/${lostReportId}/notes`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ notes, source_locale: sourceLocale }),
  })
  if (!response.ok) throw new Error(await readError(response))
  const body: unknown = await response.json()
  const row =
    body && typeof body === 'object' && 'lost' in body
      ? (body as { lost?: LostApiRow }).lost
      : null
  if (!row) throw new Error('Updated lost report was not returned')
  return toLostListItem(row)
}
